import { flattenTokens } from '../../../util';
import { colors as actualColors } from '../colors';

const placeholder = {
  _light: 'gray.500',
  _dark: 'gray.400',
};

export const colors = flattenTokens({
  layer: {
    0: {
      _light: 'white',
      _dark: 'gray.900',
      active: {
        _light: 'gray.50',
        _dark: 'gray.800',
      },
    },
    1: {
      _light: 'gray.50',
      _dark: 'gray.800',
      active: {
        _light: 'gray.100',
        _dark: 'gray.700',
      },
    },
    2: {
      _light: 'gray.100',
      _dark: 'gray.700',
      active: {
        _light: 'gray.200',
        _dark: 'gray.650',
      },
    },
    3: {
      _light: 'gray.200',
      _dark: 'gray.650',
      active: {
        _light: 'gray.300',
        _dark: 'gray.600',
      },
    },
  },
  border: {
    0: {
      _light: 'gray.200',
      _dark: 'gray.400',
    },
    1: {
      _light: 'gray.200',
      _dark: 'gray.500',
    },
    2: {
      _light: 'gray.200',
      _dark: 'gray.500',
    },
    3: {
      _light: 'gray.200',
      _dark: 'gray.500',
    },
    layer: {
      0: {
        _light: 'gray.100',
        _dark: 'gray.600',
      },
      1: {
        _light: 'gray.200',
        _dark: 'gray.600',
      },
      2: {
        _light: 'gray.300',
        _dark: 'gray.500',
      },
      3: {
        _light: 'gray.400',
        _dark: 'gray.500',
      },
    },
  },
  text: {
    primary: {
      _light: 'gray.900',
      _dark: 'gray.50',
    },
    secondary: {
      _light: 'gray.400',
      _dark: 'blue.100',
    },
    placeholder,
    disabled: {
      _light: 'blackAlpha.400',
      _dark: 'whiteAlpha.400',
    },
    link: {
      _light: 'corelight.600',
      _dark: 'blue.300',
      hover: {
        _light: 'corelight.700',
        _dark: 'blue.450',
      },
      action: {
        _light: 'corelight.800',
        _dark: 'blue.500',
        hover: {
          _light: 'corelight.900',
          _dark: 'blue.600',
        },
      },
    },
    info: {
      _dark: 'blue.300',
      _light: 'blue.700',
    },
    success: {
      _dark: 'green.300',
      _light: 'green.700',
    },
    warning: {
      _dark: 'yellow.300',
      _light: 'yellow.700',
    },
    error: {
      _dark: 'red.300',
      _light: 'red.700',
    },
    unknown: {
      _light: 'gray.400',
      _dark: 'gray.300',
    },
  },
  button: {
    text: {
      _light: 'gray.900',
      _dark: 'gray.50',
      hover: { _dark: 'gray.600', _light: 'gray.100' },
      active: { _dark: 'gray.800', _light: 'gray.200' },
      outline: { _dark: 'gray.50', _light: 'gray.900' },
      text: { _dark: 'white', _light: 'black' },
      'text-outline': { _dark: 'gray.50', _light: 'black' },
    },
    gray: {
      _dark: 'gray.700',
      _light: 'gray.50',
      hover: { _dark: 'gray.600', _light: 'gray.100' },
      active: { _dark: 'gray.800', _light: 'gray.200' },
      outline: { _dark: 'gray.500', _light: 'gray.300' },
      text: { _dark: 'white', _light: 'black' },
      'text-outline': { _dark: 'gray.50', _light: 'black' },
    },
    green: {
      _dark: 'corelight.600',
      _light: 'corelight.500',
      hover: { _dark: 'corelight.650', _light: 'corelight.600' },
      active: { _dark: 'corelight.700', _light: 'corelight.650' },
      outline: { _dark: 'corelight.600', _light: 'corelight.700' },
      text: { _dark: 'black', _light: 'black' },
      'text-outline': { _dark: 'white', _light: 'black' },
    },
    red: {
      _dark: 'red.500',
      _light: 'red.500',
      hover: { _dark: 'red.600', _light: 'red.600' },
      active: { _dark: 'red.700', _light: 'red.700' },
      outline: { _dark: 'red.400', _light: 'red.300' },
      text: { _dark: 'white', _light: 'black' },
      'text-outline': { _dark: 'red.300', _light: 'red.400' },
    },
    purple: {
      _dark: 'purple.500',
      _light: 'purple.300',
      hover: { _dark: 'purple.600', _light: 'purple.400' },
      active: { _dark: 'purple.700', _light: 'purple.500' },
      text: { _dark: 'white', _light: 'black' },
      outline: { _dark: 'purple.400', _light: 'purple.300' },
      'text-outline': { _dark: 'purple.300', _light: 'purple.500' },
    },
    yellow: {
      _dark: 'yellow.500',
      _light: 'yellow.200',
      hover: { _dark: 'yellow.600', _light: 'yellow.300' },
      active: { _dark: 'yellow.700', _light: 'yellow.400' },
      text: { _dark: 'white', _light: 'black' },
      outline: { _dark: 'yellow.400', _light: 'yellow.300' },
      'text-outline': { _dark: 'yellow.300', _light: 'yellow.500' },
    },
  } satisfies Record<
    string,
    { _dark: string; _light: string } & {
      [P in 'hover' | 'active' | 'text' | 'outline' | 'text-outline']: {
        _dark: string;
        _light: string;
      };
    }
  >,
  field: {
    _dark: 'gray.800',
    _light: 'white',
    active: {
      _dark: 'gray.900',
      _light: 'gray.50',
    },
  },
  state: {
    selected: {
      _light: 'corelight.600',
      _dark: 'corelight.600',
      bg: {
        _light: 'gray.50',
        _dark: 'gray.800',
      },
    },
    disabled: {
      _dark: 'gray.400',
      _light: 'gray.200',
    },
    unknown: {
      _light: 'gray.600',
      _dark: 'gray.500',
    },
    success: {
      _dark: 'green.700',
      _light: 'green.400',
    },
    warning: {
      _dark: 'yellow.500',
      _light: 'yellow.300',
    },
    error: {
      _dark: 'red.700',
      _light: 'red.300',
    },
    info: {
      _dark: 'blue.600',
      _light: 'blue.300',
    },
  },
  tint: {
    hover: {
      _dark: 'whiteAlpha.200',
      _light: 'blackAlpha.200',
    },
    primary: {
      _dark: `color-mix(in srgb, ${actualColors.corelight[500]} 10%, transparent)`,
      _light: `color-mix(in srgb, ${actualColors.corelight[500]} 10%, transparent)`,
    },
  },
  'chakra-placeholder-color': placeholder,
});
